import { documentTypesApi, idDocumentsApi } from '../../api';
import React, { type FC, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Button,
  Grid,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Card,
  CardContent,
  CardActions,
  useMediaQuery,
  FormControlLabel,
  Tooltip,
  Box
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  CreateEmployeeDocumentDto,
  DocumentFileEntity,
  DocumentTypeEntity,
  EmployeeDocumentEntity
} from '../../api/generated';
import { useTranslation } from 'react-i18next';
import { useFormik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import TabBase, { TabResourceProps } from './TabBase';
import AddDocumentTypeModal from 'page-sections/data-table/dataTableV2/AddDocumentTypeModal';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from 'icons/DeleteIcon';
import { v4 as uuidv4 } from 'uuid';
import UploadingOverlay from 'components/UploadOverlay';
import AddDocumentModal from 'page-sections/data-table/dataTableV2/AddDocumentModal';
import AppTextField from 'components/input-fields/AppTextField';
import { H3, Small } from 'components/Typography';
import SearchInput from 'components/input-fields/SearchInput';
import { searchDocumentByName } from 'utils/utils';
import CustomToast from 'components/CustomToast/customToast';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import CalendarInput from 'components/input-fields/CalendarInput';
import DocumentFilesModal from 'page-sections/documents/DocumentFIlesModal';
import { AxiosResponse } from 'axios';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import { ExpandMore, FileUploadOutlined, Send, DeleteForever } from '@mui/icons-material';
import { format } from 'date-fns';
import { CustomDialog } from 'components/CustomDialog';
import { useEncryption } from 'contexts/EncryptionKeyContext';
import { encryptFile } from 'utils/encryptionDecryptionAgreements';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';

export type IDFormData = Omit<CreateEmployeeDocumentDto, 'employeeId'> & {
  id?: number;
  tempKey?: string;
  filePath?: string | null;
  isVisibleForUser?: boolean;
  index?: number;
  documentName?: string;
  expiringDate: string | null;
  documentFiles?: DocumentFileEntity[];
};

const IDDocument: FC<TabResourceProps<IDFormData[]>> = ({
  data,
  onSubmit,
  isProfileCompletition = false,
  onNewDocument,
  buttonFontSize,
  userMode
}) => {
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');
  const tenant = urlParams.get('tenant');
  const { t } = useTranslation();
  const [documentTypes, setDocumentTypes] = useState<DocumentTypeEntity[]>([]);
  const [isViewing, setIsViewing] = useState<boolean>(!!data && !isProfileCompletition);
  const [openDocumentModal, setOpenDocumentModal] = useState<boolean>(false);
  const [openDocumentTypeModal, setOpenDocumentTypeModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>('');
  const initialValues: IDFormData[] = data ?? [];
  const [showToast, setShowToast] = useState<{ show: boolean; message: string; type: string }>({
    show: false,
    message: '',
    type: ''
  });
  const [usedNames, setUsedNames] = useState<Record<string, string[]>>({});
  const [openDocumentFileModal, setOpenDocumentFileModal] = useState<boolean>(false);
  const [modalFiles, setModalFiles] = useState<DocumentFileEntity[]>([]);
  const [fileModalDocumentId, setFileModalDocumentId] = useState<number | undefined>(undefined);
  const [fileModalDocumentName, setFileModalDocumentName] = useState<string>('');
  const [fileModalDocumentIsEncrypted, setFileModalDocumentIsEncrypted] = useState<boolean>(false);
  const [currentDocumentIndex, setCurrentDocumentIndex] = useState<number | undefined>(undefined);
  const { encryptionKey } = useEncryption();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const submitWithSpinner = (values: IDFormData[], _formikHelpers: FormikHelpers<IDFormData[]>): void => {
    onSubmit(values, setIsLoading);
  };

  const validationSchema = Yup.array().of(
    Yup.object({
      tempKey: Yup.string(),
      isVisibleForUser: Yup.boolean().required(''),
      documentName: Yup.string().required(
        t('common.forms.field.required', { field: t('employees.id.documentName') })
      ),
      expiringDate:
        !userMode && !isProfileCompletition
          ? Yup.string().nullable()
          : Yup.string().required(
            t('common.forms.field.required', { field: t('employees.id.documents.expirationDate') })
          ),
      filePath: Yup.string().notRequired(),
      typeName: Yup.string().required(
        t('common.forms.field.required', { field: t('employees.id.documentTypes.label') })
      )
    })
  );

  const formik = useFormik<IDFormData[]>({
    initialValues,
    validationSchema,
    onSubmit: submitWithSpinner
  });

  const [filteredItem, setFilteredItem] = useState<IDFormData[]>(formik.values);

  const closeFileModal = (): void => {
    setOpenDocumentFileModal(false);
    if (currentDocumentIndex !== undefined && formik.values[currentDocumentIndex]) {
      formik.values[currentDocumentIndex].documentFiles = modalFiles;
    }
  };

  useEffect(() => {
    const result = searchDocumentByName(formik.values, searchValue);
    setFilteredItem(result);
  }, [searchValue, formik.values]);

  const handleNameChange = (index: number, name: string | null): void => {
    formik.setFieldValue(`[${index}].documentName`, name, true);
  };

  const { handleSubmit } = formik;

  const handleApiResponse = async (
    apiFunc: Promise<AxiosResponse<DocumentFileEntity[], any>>
  ): Promise<AxiosResponse<DocumentFileEntity[], any> | undefined> => {
    try {
      const response = await apiFunc;
      setShowToast({ show: true, message: t('alerts.fileEntred'), type: 'Created' });
      return response;
    } catch (error) {
      setShowToast({ show: true, message: t('alerts.fileFailed'), type: 'Error' });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const addDocumentFile = async (files: File[], documentId: number, index: number, isEncrypted: boolean): Promise<void> => {
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      if (encryptionKey && isEncrypted) {
        const encFile = await encryptFile(files[i], encryptionKey);
        formData.append('files', encFile);
      } else if (!encryptionKey && isEncrypted) {
        setShowToast({ show: true, message: t('alerts.insertEncKey'), type: 'Error' });
      } else {
        formData.append('files', files[i]);
      }
    }
    if (!documentId) return;
    setIsLoading(true);
    const apiFunction =
      token && tenant
        ? idDocumentsApi.createFileWithToken(String(documentId), tenant, { data: formData, params: { token } })
        : idDocumentsApi.createFile(String(documentId), false, false, { data: formData });
    const newDocument = await handleApiResponse(apiFunction);
    if (!newDocument) {
      setShowToast({ show: true, message: t('alerts.fileFailed'), type: 'Error' });
      return;
    }
    const currentDocumentFiles = formik.values[index].documentFiles || [];
    const updatedFiles = currentDocumentFiles.concat(newDocument.data);
    formik.values[index].documentFiles = updatedFiles;
  };

  const fetchDocumentTypes = (): void => {
    const handleResponse = ({ data }: { data: DocumentTypeEntity[] }): void => {
      setDocumentTypes(data);
      fetchUsedNames(data);
    };
    if (token && tenant) {
      documentTypesApi.findAllWithToken(tenant, { params: { token } }).then(handleResponse);
    } else {
      documentTypesApi.findAll().then(handleResponse);
    }
  };

  const fetchUsedNames = (documentTypes: DocumentTypeEntity[]): void => {
    const nameRecord: Record<string, string[]> = {};
    for (const documentType of documentTypes) {
      if (documentType.usedDocumentsNames) {
        nameRecord[documentType.name] = documentType.usedDocumentsNames.map((data) => data.name);
      }
    }
    setUsedNames(nameRecord);
  };

  const handleIsEncryptedChange = (event: React.ChangeEvent<HTMLInputElement>, index: number): void => {
    formik.setFieldValue(`[${index}].isEncrypted`, event.target.checked, true);
  };

  const handleIsVisibleChange = (event: React.ChangeEvent<HTMLInputElement>, index: number): void => {
    formik.setFieldValue(`[${index}].isVisibleForUser`, event.target.checked, true);
  };

  const addNewDocumentFromModal = async (newDocument: IDFormData): Promise<IDFormData[]> => {
    newDocument.tempKey = uuidv4();
    if (onNewDocument) {
      newDocument = (await onNewDocument(newDocument)) as EmployeeDocumentEntity;
    }
    if (typeof newDocument.index !== 'number') {
      newDocument.index = formik.values.length;
    }
    const newArray = [...formik.values, newDocument];
    formik.setValues(newArray);
    initialValues.push(newDocument);
    setShowToast({ show: true, message: t('alerts.document'), type: 'Created' });
    return newArray;
  };

  const updateDocumentFromModal = (document: IDFormData, index: number): void => {
    formik.setFieldValue(`[${index}].documentName`, document.documentName, true);
    formik.setFieldValue(`[${index}].isVisibleForUser`, document.isVisibleForUser, true);
    formik.setFieldValue(`[${index}].typeName`, document.typeName, true);
    formik.setFieldValue(`[${index}].expiringDate`, document.expiringDate, true);
  };

  const removeDocument = (indexToRemove: number): void => {
    formik.setValues([...formik.values.slice(0, indexToRemove), ...formik.values.slice(indexToRemove + 1)]);
  };

  useEffect(() => {
    const fetchDocumentTypesFunc = async (): Promise<void> => {
      if (token && tenant) {
        documentTypesApi.findAllWithToken(tenant, { params: { token } }).then(({ data }) => {
          setDocumentTypes(data);
          fetchUsedNames(data);
        });
      } else {
        documentTypesApi.findAll().then(({ data }) => {
          setDocumentTypes(data);
          fetchUsedNames(data);
        });
      }
    };
    fetchDocumentTypesFunc();
  }, []);

  const handleCloseToast = (): void => {
    setShowToast({ show: false, message: '', type: '' });
  };

  const [deletingDocumentId, setDeletingDocumentId] = useState<number | undefined>(undefined);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState<boolean>(false);

  const handleCloseDeleteConfirmation = (): void => {
    setDeletingDocumentId(undefined);
    setDeleteConfirmationOpen(false);
  };

  const handleConfirmDelete = async (): Promise<void> => {
    if (deletingDocumentId !== undefined) {
      removeDocument(deletingDocumentId);
    }
    handleCloseDeleteConfirmation();
  };

  const handleOpenconfirmDelete = (id: number): void => {
    setDeletingDocumentId(id);
    setDeleteConfirmationOpen(true);
  };

  const IdTable = (types: DocumentTypeEntity[]): JSX.Element[] => {
    return types.map((type: DocumentTypeEntity): JSX.Element => {
      const filtered = filteredItem.filter((value: IDFormData) => value.typeName === type.name);
      if (filtered.length > 0) {
        if (!isMobile) {
          return (
            <Accordion key={type.name} disableGutters sx={{ '&:before': { display: 'none' }, margin: 2 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <H3>{type.name}</H3>
              </AccordionSummary>
              <AccordionDetails>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">{t('employees.id.documentName')}</TableCell>
                      <TableCell align="center">{t('employees.id.documents.expirationDate')}</TableCell>
                      <TableCell align="center">{t('employees.id.showFiles')}</TableCell>
                      <TableCell align="center">{t('employees.id.documentEncryption')}</TableCell>
                      <TableCell align="center">{t('employees.id.documentTypes.isVisible')}</TableCell>
                      <TableCell align="center">{t('employees.id.sendEmail')}</TableCell>
                      <TableCell align="center">{t('employees.id.delete')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.map((item: IDFormData) => (
                      <TableRow key={item.index}>
                        <TableCell align="left">
                          {isProfileCompletition || isViewing || userMode ? (
                            <Small>{item.documentName}</Small>
                          ) : (
                            <Autocomplete
                              freeSolo
                              options={usedNames[item.typeName] || []}
                              disabled={isProfileCompletition || isViewing || userMode}
                              value={item.documentName || ''}
                              onChange={(e, value) => {
                                if (item.index !== undefined) {
                                  handleNameChange(item.index, value);
                                }
                              }}
                              disableClearable
                              renderInput={(params) => (
                                <AppTextField
                                  {...params}
                                  fullWidth
                                  variant="standard"
                                  disabled={userMode}
                                  size="small"
                                  name="documentName"
                                  onChange={(e) => {
                                    if (item.index !== undefined) {
                                      if (e.target.value !== '') {
                                        handleNameChange(item.index, e.target.value);
                                      } else {
                                        handleNameChange(item.index, null);
                                      }
                                    }
                                  }}
                                  error={Boolean(item.index !== undefined && formik.errors[item.index]?.documentName)}
                                  helperText={item.index !== undefined ? formik.errors[item.index]?.documentName : ''}
                                  InputProps={{
                                    style: { fontSize: 13, fontWeight: 500, lineHeight: 1.6 }
                                  }}
                                />
                              )}
                            />
                          )}
                        </TableCell>
                        <TableCell align="left">
                          {isViewing ? (
                            <Small>
                              {item.expiringDate ? format(new Date(item.expiringDate), 'dd.MM.yyy') : <i>{t("employees.id.documents.expirationDate.placeholder")}</i>}
                            </Small>
                          ) : (
                            <CalendarInput
                              disabled={isViewing}
                              format="dd.MM.yyyy"
                              disableFuture={false}
                              localeText={{
                                fieldDayPlaceholder: () => t('DD'),
                                fieldMonthPlaceholder: () => t('MM'),
                                fieldYearPlaceholder: () => t('YYYY')
                              }}
                              value={item.expiringDate ? new Date(item.expiringDate) : null}
                              onChange={(newValue) => {
                                if (item.index !== undefined) {
                                  if (newValue && !isNaN(new Date(newValue).getTime())) {
                                    const utcDate = new Date(
                                      Date.UTC(newValue.getFullYear(), newValue.getMonth(), newValue.getDate())
                                    );
                                    formik.setFieldValue(`[${item.index}].expiringDate`, utcDate.toISOString(), true);
                                  } else {
                                    formik.setFieldValue(`[${item.index}].expiringDate`, null, true);
                                  }
                                }
                              }}
                              slotProps={{
                                textField: {
                                  variant: 'standard',
                                  size: 'small',
                                  error:
                                    item.index !== undefined &&
                                    Boolean(formik.touched[item.index]?.expiringDate && formik.errors[item.index]?.expiringDate),
                                  inputProps: {
                                    disableUnderline: true,
                                    style: { fontSize: 13, fontWeight: 500, lineHeight: 1.6 }
                                  }
                                },
                                actionBar: { actions: ['clear'] }
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {item.documentFiles && item.documentFiles.length > 0 ? (
                            <IconButton
                              size="small"
                              onClick={() => {
                                setOpenDocumentFileModal(true);
                                if (item.documentFiles) {
                                  setModalFiles(item.documentFiles);
                                }
                                if (item.id !== undefined) {
                                  setFileModalDocumentId(item.id);
                                }
                                if (item.index !== undefined) {
                                  setCurrentDocumentIndex(item.index);
                                }
                                setFileModalDocumentName(item.documentName || '');
                                const ogItem = initialValues.find((value) => value.id === item.id);
                                setFileModalDocumentIsEncrypted(ogItem ? ogItem.isEncrypted : item.isEncrypted);
                              }}
                            >
                              <FindInPageIcon />
                            </IconButton>
                          ) : (
                            <IconButton component="label" size="small">
                              <FileUploadOutlined />
                              <input
                                type="file"
                                style={{ display: 'none' }}
                                onChange={(e) => {
                                  if (e.target.files && item.id !== undefined && item.index !== undefined) {
                                    addDocumentFile(Array.from(e.target.files), item.id, item.index, item.isEncrypted);
                                  }
                                }}
                                multiple
                              />
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {!isProfileCompletition && !userMode && (
                            <Tooltip title={t('employees.id.toggleEncryption')}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    disabled={isViewing || item.isVisibleForUser || !encryptionKey}
                                    checked={!!(item.isEncrypted && !item.isVisibleForUser)}
                                    onChange={(e) => {
                                      if (item.index !== undefined) {
                                        handleIsEncryptedChange(e, item.index);
                                      }
                                    }}
                                    inputProps={{ 'aria-label': 'encryption toggle' }}
                                  />
                                }
                                label={<LockIcon fontSize="small" />}
                              />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {!isProfileCompletition && !userMode && (
                            <Tooltip title={t('employees.id.toggleVisibility')}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    disabled={isViewing || item.isEncrypted}
                                    checked={!!(item.isVisibleForUser && !item.isEncrypted)}
                                    onChange={(e) => {
                                      if (item.index !== undefined) {
                                        handleIsVisibleChange(e, item.index);
                                      }
                                    }}
                                    inputProps={{ 'aria-label': 'visibility toggle' }}
                                  />
                                }
                                label={<VisibilityIcon fontSize="small" />}
                              />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            disabled={!item.isVisibleForUser || item.isEncrypted}
                            onClick={async () => {
                              if (item.id !== undefined) {
                                const result = await idDocumentsApi.sendEmail(item.id, true);
                                if (result.status === 201) {
                                  setShowToast({ show: true, message: t('alerts.fileSent'), type: 'Created' });
                                } else {
                                  setShowToast({ show: true, message: t('alerts.error'), type: 'Error' });
                                }
                              }
                            }}
                          >
                            <Send />
                          </IconButton>
                        </TableCell>
                        <TableCell align="center">
                          {!isProfileCompletition && !userMode && (
                            <IconButton
                              size="small"
                              color="error"
                              disabled={isViewing}
                              onClick={() => {
                                if (!isViewing && item.index !== undefined) {
                                  handleOpenconfirmDelete(item.index);
                                }
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </AccordionDetails>
            </Accordion>
          );
        } else {
          return (
            <React.Fragment key={type.name}>
              <H3 sx={{ margin: 2 }}>{type.name}</H3>
              {filtered.map((item: IDFormData) => {
                const mobileIcons: JSX.Element[] = [];
                mobileIcons.push(
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (item.documentFiles && item.documentFiles.length > 0) {
                        setOpenDocumentFileModal(true);
                        if (item.documentFiles) {
                          setModalFiles(item.documentFiles);
                        }
                        if (item.id !== undefined) {
                          setFileModalDocumentId(item.id);
                        }
                        if (item.index !== undefined) {
                          setCurrentDocumentIndex(item.index);
                        }
                        setFileModalDocumentName(item.documentName || '');
                        const ogItem = initialValues.find((value) => value.id === item.id);
                        setFileModalDocumentIsEncrypted(ogItem ? ogItem.isEncrypted : item.isEncrypted);
                      }
                    }}
                    key="file"
                  >
                    {item.documentFiles && item.documentFiles.length > 0 ? (
                      <FindInPageIcon />
                    ) : (
                      <>
                        <IconButton component="label" size="small" key="file">
                          <FileUploadOutlined />
                          <input
                            type="file"
                            hidden
                            onChange={(e) => {
                              if (e.target.files && item.id !== undefined && item.index !== undefined) {
                                addDocumentFile(Array.from(e.target.files), item.id, item.index, item.isEncrypted);
                              }
                            }}
                            multiple
                          />
                        </IconButton>
                      </>
                    )}
                  </IconButton>
                );
                if (!isProfileCompletition && !userMode) {
                  mobileIcons.push(
                    <Tooltip title={t('employees.id.toggleEncryption')} key="encryption">
                      <FormControlLabel
                        control={
                          <Switch
                            disabled={isViewing || item.isVisibleForUser || !encryptionKey}
                            checked={!!(item.isEncrypted && !item.isVisibleForUser)}
                            onChange={(e) => {
                              if (item.index !== undefined) {
                                handleIsEncryptedChange(e, item.index);
                              }
                            }}
                            inputProps={{ 'aria-label': 'encryption toggle' }}
                          />
                        }
                        label={<LockIcon fontSize="small" />}
                      />
                    </Tooltip>
                  );
                  mobileIcons.push(
                    <Tooltip title={t('employees.id.toggleVisibility')} key="visibility">
                      <FormControlLabel
                        control={
                          <Switch
                            disabled={isViewing || item.isEncrypted}
                            checked={!!(item.isVisibleForUser && !item.isEncrypted)}
                            onChange={(e) => {
                              if (item.index !== undefined) {
                                handleIsVisibleChange(e, item.index);
                              }
                            }}
                            inputProps={{ 'aria-label': 'visibility toggle' }}
                          />
                        }
                        label={<VisibilityIcon fontSize="small" />}
                      />
                    </Tooltip>
                  );
                }
                if (!isProfileCompletition && !userMode) {
                  mobileIcons.push(
                    <IconButton
                      size="small"
                      disabled={!item.isVisibleForUser || item.isEncrypted}
                      onClick={async () => {
                        if (item.id !== undefined) {
                          const result = await idDocumentsApi.sendEmail(item.id, true);
                          if (result.status === 201) {
                            setShowToast({ show: true, message: t('alerts.fileSent'), type: 'Created' });
                          } else {
                            setShowToast({ show: true, message: t('alerts.error'), type: 'Error' });
                          }
                        }
                      }}
                      key="send"
                    >
                      <Send />
                    </IconButton>
                  );
                  mobileIcons.push(
                    <IconButton
                      size="small"
                      color="error"
                      disabled={isViewing}
                      onClick={() => {
                        if (!isViewing && item.index !== undefined) {
                          handleOpenconfirmDelete(item.index);
                        }
                      }}
                      key="delete"
                    >
                      <DeleteIcon />
                    </IconButton>
                  );
                }
                return (
                  <Card
                    key={item.index}
                    sx={{
                      margin: 2,
                      boxShadow: 3,
                      borderRadius: 2,
                      backgroundColor: theme.palette.secondary.light
                    }}
                  >
                    <CardContent>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          {isProfileCompletition || isViewing || userMode ? (
                            <Small style={{ fontWeight: 700, fontSize: 20 }}>{item.documentName}</Small>
                          ) : (
                            <Autocomplete
                              freeSolo
                              options={usedNames[item.typeName] || []}
                              disabled={isProfileCompletition || isViewing || userMode}
                              value={item.documentName || ''}
                              onChange={(e, value) => {
                                if (item.index !== undefined) {
                                  handleNameChange(item.index, value);
                                }
                              }}
                              disableClearable
                              renderInput={(params) => (
                                <AppTextField
                                  {...params}
                                  fullWidth
                                  variant="standard"
                                  disabled={userMode}
                                  size="small"
                                  name="documentName"
                                  onChange={(e) => {
                                    if (item.index !== undefined) {
                                      if (e.target.value !== '') {
                                        handleNameChange(item.index, e.target.value);
                                      } else {
                                        handleNameChange(item.index, null);
                                      }
                                    }
                                  }}
                                  error={Boolean(item.index !== undefined && formik.errors[item.index]?.documentName)}
                                  helperText={item.index !== undefined ? formik.errors[item.index]?.documentName : ''}
                                  InputProps={{
                                    style: { fontSize: 13, fontWeight: 500, lineHeight: 1.6 }
                                  }}
                                />
                              )}
                            />
                          )}
                        </Grid>
                        <Grid item xs={12}>
                          {isViewing ? (
                            <Small>
                              {item.expiringDate
                                ? format(new Date(item.expiringDate), 'dd.MM.yyy')
                                : <i>{t("employees.id.documents.expirationDate.placeholder")}</i>}
                            </Small>
                          ) : (
                            <CalendarInput
                              disabled={isViewing}
                              format="dd.MM.yyyy"
                              disableFuture={false}
                              localeText={{
                                fieldDayPlaceholder: () => t('DD'),
                                fieldMonthPlaceholder: () => t('MM'),
                                fieldYearPlaceholder: () => t('YYYY')
                              }}
                              value={item.expiringDate ? new Date(item.expiringDate) : null}
                              onChange={(newValue) => {
                                if (item.index !== undefined) {
                                  if (newValue && !isNaN(new Date(newValue).getTime())) {
                                    const utcDate = new Date(
                                      Date.UTC(newValue.getFullYear(), newValue.getMonth(), newValue.getDate())
                                    );
                                    formik.setFieldValue(`[${item.index}].expiringDate`, utcDate.toISOString(), true);
                                  } else {
                                    formik.setFieldValue(`[${item.index}].expiringDate`, null, true);
                                  }
                                }
                              }}
                              slotProps={{
                                textField: {
                                  variant: 'standard',
                                  size: 'small',
                                  error:
                                    item.index !== undefined &&
                                    Boolean(formik.touched[item.index]?.expiringDate && formik.errors[item.index]?.expiringDate),
                                  inputProps: {
                                    disableUnderline: true,
                                    style: { fontSize: 13, fontWeight: 500, lineHeight: 1.6 }
                                  }
                                },
                                actionBar: { actions: ['clear'] }
                              }}
                            />
                          )}
                        </Grid>
                      </Grid>
                    </CardContent>
                    <CardActions>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(3, 1fr)',
                          gap: 1,
                          justifyItems: 'center',
                          width: '100%'
                        }}
                      >
                        {mobileIcons}
                      </Box>
                    </CardActions>
                  </Card>
                );
              })}
            </React.Fragment>
          );
        }
      }
      return <React.Fragment key={type.name} />;
    });
  };

  return (
    <TabBase
      title={t('employees.id.title')}
      setIsViewing={setIsViewing}
      isViewing={isViewing}
      handleSubmit={handleSubmit}
      isProfileCompletition={isProfileCompletition}
    >
      {showToast.show && (
        <CustomToast
          duration={3000}
          onClose={handleCloseToast}
          message={showToast.message}
          icon={showToast.type === 'Created' ? <CheckCircleOutlineRoundedIcon /> : ''}
        />
      )}
      {isLoading && <UploadingOverlay />}
      {fileModalDocumentId !== undefined && (
        <DocumentFilesModal
          onClose={closeFileModal}
          onDocumentRemove={(id, index) => {
            if (isProfileCompletition) {
              if (tenant && token) {
                idDocumentsApi.removeFileWithToken(String(id), tenant, { params: { token } });
              }
            } else {
              idDocumentsApi.removeFile(String(id));
            }
            if (currentDocumentIndex !== undefined && formik.values[currentDocumentIndex]) {
              setModalFiles([...modalFiles.slice(0, index), ...modalFiles.slice(index + 1)]);
            }
          }}
          isCompiledByEmployee={!!(token && tenant)}
          open={openDocumentFileModal}
          data={modalFiles}
          isEncrypted={fileModalDocumentIsEncrypted}
          setData={setModalFiles}
          documentId={fileModalDocumentId}
          documentName={fileModalDocumentName || ''}
          setIsLoading={setIsLoading}
          isLoading={isLoading}
          setShowToastCallback={setShowToast}
          userMode={userMode}
        />
      )}
      {!userMode && (
        <AddDocumentModal
          addNewDocument={addNewDocumentFromModal}
          updateDocument={updateDocumentFromModal}
          open={openDocumentModal}
          documentTypes={documentTypes}
          setIsLoading={setIsLoading}
          onClose={() => {
            setOpenDocumentModal(false);
          }}
          setCurrentDocumentIndex={setCurrentDocumentIndex}
          setFileModalDocumentId={setFileModalDocumentId}
          setModalFiles={setModalFiles}
          setFileModalDocumentName={setFileModalDocumentName}
          setFileModalDocumentIsEncrypted={setFileModalDocumentIsEncrypted}
          setOpenDocumentFileModal={setOpenDocumentFileModal}
        />
      )}
      <CustomDialog
        open={deleteConfirmationOpen}
        onClose={handleCloseDeleteConfirmation}
        onConfirm={handleConfirmDelete}
        title={t('dialog.title', { item: t('documents.itemName').toLocaleLowerCase() })}
        content={t('dialog.document.delete')}
        icon={<DeleteForever />}
        confirmText={t('dialog.confirm')}
        confirmColor="error"
        cancelColor="primary"
      />
      <AddDocumentTypeModal
        open={openDocumentTypeModal}
        onClose={() => {
          setOpenDocumentTypeModal(false);
          fetchDocumentTypes();
        }}
        setShowToastCallback={setShowToast}
      />
      {!userMode && (
        <SearchInput
          bordered={false}
          placeholder={t('employees.id.search')}
          onChange={(e) => {
            setSearchValue(e.target.value);
          }}
        />
      )}
      {IdTable(documentTypes)}
      {!isProfileCompletition && !userMode && (
        <Grid container spacing={2} marginTop={2} borderTop={1} borderColor="lightgray">
          <Grid item xs={5} sm={5}>
            <Button
              sx={{ textAlign: 'left', fontSize: buttonFontSize }}
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => {
                setOpenDocumentModal(true);
              }}
            >
              {t('employees.contract.contractFile')}
            </Button>
          </Grid>
          <Grid item xs={1} sm={0} />
          <Grid item xs={5} sm={5}>
            <Button
              sx={{ textAlign: 'left', fontSize: buttonFontSize }}
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => {
                setOpenDocumentTypeModal(true);
              }}
            >
              {t('employees.id.documentTypes.add')}
            </Button>
          </Grid>
        </Grid>
      )}
    </TabBase>
  );
};

export default IDDocument;
